/* Order Register toolbar: Queries filter (Open / Follow-up due / Solved) + Query Board. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QueryHoverById } from "@/components/QueryHover";
import { API, getAuthHeader } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquareWarning, Download, BellRing, BellOff } from "lucide-react";
import { openParcelQuery } from "@/components/ParcelQueryDialog";
import { RepeatProblemAlert, QueryTeamStats, PartnerReplyTimes } from "@/components/QueryInsights";
import { PhotoReminderBadge } from "@/components/PhotoReminders";
import { PhotoRequestsButton } from "@/components/PhotoRequestsList";
import { CorrectionReportButton } from "@/components/CorrectionReportButton";
import { CorrectionTrendButton } from "@/components/CorrectionTrendButton";

const age = (iso) => { const d = Math.floor((Date.now() - new Date(iso)) / 86400000); return d ? `${d}d` : "today"; };

const Bars = ({ title, data, color, testId }) => {
  const max = Math.max(1, ...data.map(([, n]) => n));
  return (
    <div className="flex-1 min-w-[220px] rounded border border-slate-200 p-2" data-testid={testId}>
      <div className="text-[11px] font-semibold text-slate-600 mb-1">{title}</div>
      {data.length ? data.map(([k, n], i) => (
        <div key={k} className="flex items-center gap-2 text-[11px] py-0.5" data-testid={`${testId}-${i}`}>
          <span className="w-36 truncate text-slate-700" title={k}>{k}</span>
          <span className="flex-1 h-3 rounded bg-slate-100 overflow-hidden">
            <span className={`block h-full ${color}`} style={{ width: `${(n / max) * 100}%` }} />
          </span>
          <b className="w-6 text-right">{n}</b>
        </div>
      )) : <div className="text-[11px] text-slate-400">No data</div>}
    </div>
  );
};

const Board = ({ open, onClose, initialState = "open" }) => {
  const [state, setState] = useState(initialState);
  useEffect(() => { if (open) setState(initialState); }, [open, initialState]);
  const [d, setD] = useState(null);
  const [range, setRange] = useState("7");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState("opened");
  const params = useMemo(() => ({ state, sort, ...(range === "custom" ? { date_from: from, date_to: to, days: 0 } : { days: Number(range) }) }),
    [state, sort, range, from, to]);
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/order-register/queries`, { headers: getAuthHeader(), params }); setD(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to load queries"); }
  }, [params]);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/order-register/queries/export.xlsx`, { headers: getAuthHeader(), params, responseType: "blob" });
      const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "Query_Board.xlsx";
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Download failed"); }
  };
  useEffect(() => { if (open) load(); }, [open, load]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="pq-board">
        <DialogHeader><DialogTitle>Query Board</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-1.5">
          {[["open", "Open"], ["due", "Follow-up due"], ["solved", "Solved – to process"], ["processed", "Processed"], ["all", "All"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setState(k)} data-testid={`pq-board-tab-${k}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${state === k ? "bg-slate-900 text-white" : "bg-white text-slate-700"}`}>
              {l} {d && <span className="opacity-70">({d.counts[k]})</span>}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="pq-board-controls">
          <select value={range} onChange={(e) => setRange(e.target.value)} className="h-7 rounded border px-2" data-testid="pq-board-range">
            <option value="7">Opened: last 7 days</option>
            <option value="30">Opened: last 30 days</option>
            <option value="0">Opened: all time</option>
            <option value="custom">Custom date range…</option>
          </select>
          {range === "custom" && (<>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-7 rounded border px-1" data-testid="pq-board-from" />
            <span>to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-7 rounded border px-1" data-testid="pq-board-to" />
          </>)}
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-7 rounded border px-2" data-testid="pq-board-sort">
            <option value="opened">Sort: oldest first</option>
            <option value="status">Sort: status</option>
            <option value="type">Sort: query type</option>
            <option value="customer">Sort: customer</option>
            <option value="service">Sort: service</option>
            <option value="outcome">Sort: outcome</option>
            <option value="followup">Sort: next follow-up</option>
          </select>
          <span className="text-[10px] text-slate-400">Still-open queries are always included.</span>
          <button type="button" onClick={download} data-testid="pq-board-download"
            className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 font-semibold text-white hover:bg-emerald-700">
            <Download className="w-3.5 h-3.5" /> Download Excel
          </button>
        </div>
        {d && (
          <>
            <RepeatProblemAlert alerts={d.repeat_alerts} />
            <div className="flex flex-wrap gap-2" data-testid="pq-board-charts">
              <Bars title="Top query types" data={d.stats?.types || []} color="bg-orange-500" testId="pq-chart-types" />
              <Bars title="Top products in queries" data={d.stats?.products || []} color="bg-indigo-500" testId="pq-chart-products" />
            </div>
            <QueryTeamStats rows={d.team_stats} />
            <PartnerReplyTimes />
            <div className="flex flex-wrap gap-1 text-[11px]" data-testid="pq-board-users">
              {d.per_user.map((u) => <span key={u.name} className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{u.name}: {u.entries} updates</span>)}
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-slate-600"><tr>
                {["Age", "Customer", "Code", "Date", "Service", "Type", "Last note", "Follow-up / Outcome"].map((h) => <th key={h} className="p-2 text-left">{h}</th>)}</tr></thead>
              <tbody>
                {d.items.map((q, i) => {
                  const last = (q.entries || [])[q.entries.length - 1] || {};
                  return (
                    <QueryHoverById key={q.id} qid={q.id} side="bottom">
                    <tr className={`border-t cursor-pointer hover:bg-amber-50 ${q.state === "due" ? "bg-red-50" : ""}`}
                      onClick={() => openParcelQuery(q.row_ids[0])} data-testid={`pq-board-row-${i}`}>
                      <td className="p-2">{age(q.created_at)}</td>
                      <td className="p-2 font-medium">{q.customer_name}</td>
                      <td className="p-2">{q.code}</td><td className="p-2">{q.date}</td><td className="p-2">{q.mode}</td>
                      <td className="p-2 text-orange-700">{q.type}</td>
                      <td className="p-2 max-w-[260px] truncate" title={last.text}><b>{last.by_name}:</b> {last.text}</td>
                      <td className={`p-2 ${q.state === "due" ? "text-red-600 font-semibold" : q.state === "solved" ? "text-emerald-700" : q.state === "processed" ? "text-blue-700" : ""}`}>
                        {q.outcome || (q.next_followup_at ? new Date(q.next_followup_at).toLocaleString() : "—")}
                        {q.processed_at && <div className="text-[10px]">✓ {q.processed_by_name}</div>}
                      </td>
                    </tr>
                    </QueryHoverById>
                  );
                })}
                {!d.items.length && <tr><td colSpan={8} className="p-6 text-center text-slate-400">Nothing here</td></tr>}
              </tbody>
            </table>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const chime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1320].forEach((f, i) => {
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.frequency.value = f; o.type = "sine"; o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime + i * 0.18;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.25, t + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      o.start(t); o.stop(t + 0.4);
    });
  } catch { /* audio blocked */ }
};

const PartnerWaitingBadge = ({ active, onPick }) => {
  const [n, setN] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem("pq_partner_mute") === "1");
  const prev = useRef(null);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  useEffect(() => {
    const load = () => axios.get(`${API}/order-register/queries/partner-waiting`, { headers: getAuthHeader() })
      .then((r) => {
        const c = r.data.count || 0;
        if (prev.current !== null && c > prev.current && !mutedRef.current) chime();
        prev.current = c; setN(c);
      }).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);
  const toggleMute = () => { const m = !muted; setMuted(m); localStorage.setItem("pq_partner_mute", m ? "1" : "0"); if (!m) chime(); };
  return (<span className="inline-flex items-center gap-0.5">
    {(n > 0 || active) && <button type="button" onClick={onPick} title="Partner queries waiting for your reply — tap to show only these parcels"
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-fuchsia-600 bg-fuchsia-600 text-white" : "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-800 animate-pulse"}`}
      data-testid="or-partner-query-badge">
      Partner queries <span className={`rounded-full px-1.5 ${active ? "bg-white text-fuchsia-700" : "bg-fuchsia-600 text-white"}`} data-testid="or-partner-query-count">{n}</span>
    </button>}
    <button type="button" onClick={toggleMute} title={muted ? "Sound off for new partner queries — tap to turn on" : "Sound on for new partner queries — tap to mute"}
      className="rounded-full p-1 text-fuchsia-700 hover:bg-fuchsia-50" data-testid="or-partner-query-mute">
      {muted ? <BellOff className="h-3.5 w-3.5" /> : <BellRing className="h-3.5 w-3.5" />}</button>
  </span>);
};

const HandbackBadge = ({ active, onPick }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const load = () => axios.get(`${API}/order-register/pp/handback/count`, { headers: getAuthHeader() })
      .then((r) => setN(r.data.count || 0)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);
  if (!n && !active) return null;
  return (
    <button type="button" onClick={onPick} title="Parcels the Pending Parcel desk handed back to your service — tap to show only these"
      className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-semibold ${active ? "border-orange-600 bg-orange-600 text-white" : "border-orange-400 bg-orange-50 text-orange-800"}`}
      data-testid="or-handback-badge">
      Handed back <span className={`rounded-full px-1.5 ${active ? "bg-white text-orange-700" : "bg-orange-600 text-white"}`} data-testid="or-handback-count">{n}</span>
    </button>
  );
};

export const QueryToolbar = ({ value, onChange }) => {
  const [board, setBoard] = useState(false);
  const [boardTab, setBoardTab] = useState("open");
  useEffect(() => {  // ?qboard=7 deep link (weekly digest bell) → All tab
    const u = new URL(window.location.href);
    if (u.searchParams.get("qboard")) {
      setBoardTab("all"); setBoard(true); u.searchParams.delete("qboard"); window.history.replaceState(null, "", u);
    }
  }, []);
  return (
    <>
      <select value={value} onChange={(e) => onChange(e.target.value)} title="Filter parcels by customer query"
        className={`h-7 rounded-full border px-2 text-[11px] font-semibold ${value ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-700"}`}
        data-testid="or-query-filter">
        <option value="">Queries: all</option>
        <option value="open">Query open</option>
        <option value="due">Follow-up due</option>
        <option value="solved">Solved – to process</option>
        <option value="processed">Processed</option>
        <option value="partner">Partner – needs reply</option>
        <option value="photos">Missing packing photos</option>
        <option value="handback">Handed back by PP desk</option>
      </select>
      <HandbackBadge active={value === "handback"} onPick={() => onChange(value === "handback" ? "" : "handback")} />
      <PartnerWaitingBadge active={value === "partner"} onPick={() => onChange(value === "partner" ? "" : "partner")} />
      <PhotoReminderBadge />
      <PhotoRequestsButton />
      <CorrectionReportButton />
      <CorrectionTrendButton />
      <button type="button" onClick={() => { setBoardTab("open"); setBoard(true); }} data-testid="or-query-board-btn"
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-orange-300 bg-orange-50 px-2.5 py-1 text-[11px] font-semibold text-orange-800 hover:bg-orange-100">
        <MessageSquareWarning className="w-3.5 h-3.5" /> Query Board
      </button>
      <Board open={board} initialState={boardTab} onClose={() => { setBoard(false); setBoardTab("open"); }} />
    </>
  );
};
